import React from 'react';
import { Award, BookOpen, Users } from 'lucide-react';
import ErrorBoundary from './ErrorBoundary';
import { useLazyLoading } from './ghost/hooks/useLazyLoading';
import instructorImage from '../assets/instructor.jpg';
import tomasImage from '../assets/tomas.png';
import andreaImage from '../assets/Andrea3.jpg';
import { motion } from 'framer-motion';
import { Testimonial, testimonials } from '@/utils/testimonials';

const TestimonialsCarousel = React.lazy(() => import('./TestimonialsCarousel'));

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-card text-card-foreground rounded-xl shadow-md p-6"
    >
      <div className="flex items-center gap-4 mb-4">
        <motion.img
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          src={testimonial.image}
          alt={testimonial.name}
          className="w-12 h-12 rounded-full"
        />
        <div>
          <motion.h4
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-semibold text-foreground"
          >
            {testimonial.name}
          </motion.h4>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-sm text-muted-foreground"
          >
            {testimonial.role}, {testimonial.company}
          </motion.p>
        </div>
      </div>
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="text-muted-foreground italic"
      >
        {testimonial.content}
      </motion.p>
    </motion.div>
  );
}

export default function Instructor() {
  // Avoid pulling Firebase into the initial bundle via TestimonialsCarousel (Firestore-backed).
  // Load the carousel only when the section is near the viewport and fall back to static testimonials.
  const { elementRef, isIntersecting } = useLazyLoading<HTMLDivElement>({ rootMargin: '300px' });

  return (
    <section id="instructor" className="py-24 bg-muted">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Team Section */}
          <div className="mb-12">
            {/* Header */}
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-3xl font-bold text-foreground mb-4"
              >
                Náš tím inštruktorov
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="text-lg text-muted-foreground max-w-2xl mx-auto"
              >
                Praktické zručnosti a vedomosti s okamžitým využitím v praxi. Kurzy vychádzajú z reálnych skúseností, nie teoretických konceptov.
              </motion.p>
            </div>
            
            {/* Team Members Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* Marián Čuprík */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.5
                }}
                className="text-center p-8 bg-card rounded-xl shadow-lg min-h-[500px] flex flex-col items-center justify-start cursor-pointer hover:shadow-2xl hover:shadow-blue-500/20 hover:scale-105 hover:-translate-y-2 transition-all duration-500 ease-out"
              >
                <motion.div 
                  className="w-64 h-64 mb-8 rounded-full overflow-hidden border-4 p-1 flex-shrink-0"
                  animate={{
                    background: [
                      "linear-gradient(0deg, #2563eb, #9333ea)",
                      "linear-gradient(90deg, #3b82f6, #a855f7)",
                      "linear-gradient(180deg, #1d4ed8, #7c3aed)",
                      "linear-gradient(270deg, #2563eb, #8b5cf6)",
                      "linear-gradient(360deg, #2563eb, #9333ea)"
                    ]
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                   <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center">
                     <img
                        src={instructorImage}
                        alt="Marián Čuprík"
                        className="w-full h-full object-cover object-[center_-35%] scale-[2.2]"
                      />
                   </div>
                 </motion.div>
                <div className="flex flex-col items-center">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Marián Čuprík</h3>
                  <p className="text-primary font-semibold mb-3">Inštruktor</p>
                  <p className="text-sm text-muted-foreground mb-4">Advokát a technologický profesionál s rokmi skúseností s AI. Spája právnické vzdelanie s hlbokou expertízou v oblasti AI technológií.</p>
                </div>
              </motion.div>
              
              {/* Tomáš Vince */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.6
                }}
                className="text-center p-8 bg-card rounded-xl shadow-lg min-h-[500px] flex flex-col items-center justify-start cursor-pointer hover:shadow-2xl hover:shadow-blue-500/20 hover:scale-105 hover:-translate-y-2 transition-all duration-500 ease-out"
              >
                <motion.div 
                  className="w-64 h-64 mb-8 rounded-full overflow-hidden border-4 p-1 flex-shrink-0"
                  animate={{
                    background: [
                      "linear-gradient(0deg, #2563eb, #9333ea)",
                      "linear-gradient(90deg, #3b82f6, #a855f7)",
                      "linear-gradient(180deg, #1d4ed8, #7c3aed)",
                      "linear-gradient(270deg, #2563eb, #8b5cf6)",
                      "linear-gradient(360deg, #2563eb, #9333ea)"
                    ]
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                   <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center">
                     <img
                           src={tomasImage}
                           alt="Tomáš Vince"
                           className="w-full h-full object-cover object-[center_-29%] scale-[2.0]"
                         />
                   </div>
                 </motion.div>
                <div className="flex flex-col items-center">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Tomáš Vince</h3>
                  <p className="text-primary font-semibold mb-3">Inštruktor</p>
                  <p className="text-sm text-muted-foreground mb-4">Špecialista na praktické aplikácie AI v biznise. Zameriava sa na implementáciu AI riešení v reálnych projektoch.</p>
                </div>
              </motion.div>
              
              {/* Andrea Kapalková */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.7
                }}
                className="text-center p-8 bg-card rounded-xl shadow-lg min-h-[500px] flex flex-col items-center justify-start cursor-pointer hover:shadow-2xl hover:shadow-purple-500/20 hover:scale-105 hover:-translate-y-2 transition-all duration-500 ease-out"
              >
                <motion.div 
                  className="w-64 h-64 mb-8 rounded-full overflow-hidden border-4 p-1 flex-shrink-0"
                  animate={{
                    background: [
                      "linear-gradient(0deg, #2563eb, #9333ea)",
                      "linear-gradient(90deg, #3b82f6, #a855f7)",
                      "linear-gradient(180deg, #1d4ed8, #7c3aed)",
                      "linear-gradient(270deg, #2563eb, #8b5cf6)",
                      "linear-gradient(360deg, #2563eb, #9333ea)"
                    ]
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                   <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center">
                     <img
                          src={andreaImage}
                          alt="Andrea Kapalková"
                          className="w-full h-full object-cover object-[center_45%] scale-[1.15]"
                        />
                   </div>
                 </motion.div>
                <div className="flex flex-col items-center">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Andrea Kapalková</h3>
                  <p className="text-primary font-semibold mb-3">Marketing & Dizajn</p>
                  <p className="text-sm text-muted-foreground mb-4">Špecialistka na Marketing a Design s dlhoročnými skúsenosťami zo zahraničných a lokálnych technologických firiem.</p>
                </div>
              </motion.div>
            </div>
            
            {/* Stats Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: BookOpen, label: "10+ rokov profesionálnych skúseností" },
                { icon: Users, label: "500 absolventov kurzov" },
                { icon: Award, label: "Praktická implementácia AI riešení" }
              ].map(({ icon: Icon, label }, index) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.8 + 0.1 * index }}
                  className="text-center"
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="bg-card text-card-foreground rounded-lg p-4 shadow-sm h-24 flex flex-col justify-center items-center"
                  >
                    <Icon className="w-8 h-8 mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium text-center">{label}</p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24"
        >
          <div ref={elementRef}>
            <ErrorBoundary
              fallback={
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-center text-foreground">Referencie</h3>
                  <div className="grid gap-6 md:grid-cols-3">
                    {testimonials.map((t) => (
                      <TestimonialCard key={t.id} testimonial={t} />
                    ))}
                  </div>
                </div>
              }
            >
              {isIntersecting ? (
                <React.Suspense
                  fallback={
                    <div className="space-y-6">
                      <h3 className="text-2xl font-semibold text-center text-foreground">Referencie</h3>
                      <div className="grid gap-6 md:grid-cols-3">
                        {testimonials.map((t) => (
                          <TestimonialCard key={t.id} testimonial={t} />
                        ))}
                      </div>
                    </div>
                  }
                >
                  <TestimonialsCarousel />
                </React.Suspense>
              ) : (
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-center text-foreground">Referencie</h3>
                  <div className="grid gap-6 md:grid-cols-3">
                    {testimonials.map((t) => (
                      <TestimonialCard key={t.id} testimonial={t} />
                    ))}
                  </div>
                </div>
              )}
            </ErrorBoundary>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
